import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AsyncBoundary, Drawer, Notice } from '@kollez/design-system';
import { useInterviews, useInterviewCandidatePreview, type InterviewCandidatePreview } from '@/lib/queries';
import { formatIndianDate } from '@kollez/shared';

/**
 * /company/panel/interviews — panel workspace for technical_panel / hiring_manager /
 * recruiter roles. Server auto-scopes `technical_panel` to `interviewers[].user =
 * self`; other roles opt in via ?mine=true. Feedback is submitted from the
 * shared /interviews/:id detail page (`interview:conduct` permission).
 */
export function CompanyPanelInterviewsPage() {
  const [status, setStatus] = useState<'' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled'>('scheduled');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const list = useInterviews({ mine: true, status: status || undefined, sortBy: 'scheduledAt', sortDir: 'asc', limit: 100 });
  const rows = list.data?.interviews ?? [];
  const summary = list.data?.summary?.byStatus ?? {};

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Panel workspace</p>
          <h1 className="h-page">My interviews</h1>
          <p className="subhead">
            Every interview you're on the panel for. Click a row to open the interview
            detail and record feedback (rating, strengths, weaknesses, recommendation).
          </p>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <div className="card kpi"><span className="kpi-label">Scheduled</span><span className="kpi-value">{summary.scheduled ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">In progress</span><span className="kpi-value">{summary.in_progress ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Completed</span><span className="kpi-value">{summary.completed ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Cancelled</span><span className="kpi-value">{summary.cancelled ?? 0}</span></div>
      </div>

      <div className="card">
        <div className="filterbar">
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="">Any status</option>
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <span className="fb-count">{rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No panels assigned"
          emptyHint="The TPO adds you to a panel when they schedule interviews. Check with them if you expected one."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Round</th>
                  <th>Candidate</th>
                  <th>Panel</th>
                  <th>Room / Link</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const student = (r as { student?: { fullName?: string } | string | null }).student;
                  const studentName = typeof student === 'object' && student ? (student.fullName ?? '—') : '—';
                  return (
                    <tr key={r._id}>
                      <td className="cell-sub">
                        {r.scheduledAt ? formatIndianDate(new Date(r.scheduledAt)) : '—'}
                        {r.scheduledAt && (
                          <div className="cell-sub">
                            {new Date(r.scheduledAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </td>
                      <td className="cell-sub">Round {r.roundNumber} · {r.roundType ?? '—'}</td>
                      <td className="cell-title">{studentName}</td>
                      <td className="cell-sub">{r.panel ?? '—'}</td>
                      <td className="cell-sub">
                        {r.meetingLink
                          ? <a className="link" href={r.meetingLink} target="_blank" rel="noreferrer">Meeting link</a>
                          : r.room ?? '—'}
                      </td>
                      <td>
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => setPreviewId(r._id)}
                            title="Candidate preview"
                          >
                            Preview
                          </button>
                          <Link className="btn btn-sm" to={`/interviews/${r._id}`}>Open</Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Drawer
        open={Boolean(previewId)}
        onClose={() => setPreviewId(null)}
        title="Candidate preview"
        size="lg"
      >
        {previewId && <CandidatePreviewBody interviewId={previewId} />}
      </Drawer>

      <Notice tone="info">
        <b>Feedback goes on the interview detail page.</b> Open a row, and use the panel
        form to submit rating + strengths + weaknesses + recommendation + result.
        Everything writes to <code>Application.lastInterview</code> and the drive-room stream.
      </Notice>
    </div>
  );
}

/**
 * Consumes GET /interviews/:id/candidates-preview (Pass 6 relationship endpoint).
 * Server-scoped to interviewers on this row + tenant TPO + partner recruiters.
 * Compact read-only card set so a panel can see who's walking in without leaving
 * the queue.
 */
function CandidatePreviewBody({ interviewId }: { interviewId: string }) {
  const q = useInterviewCandidatePreview(interviewId);
  if (q.isLoading) return <p className="cell-sub">Loading…</p>;
  if (q.error || !q.data) return <p className="cell-sub">Couldn't load the candidate preview.</p>;
  const d: InterviewCandidatePreview = q.data;
  const cand = d.candidate;
  const app = d.application;
  const resume = d.resume;
  const skillLabels = (cand.skills ?? []).map((s) => (typeof s === 'string' ? s : (s.name ?? s.slug ?? ''))).filter(Boolean).slice(0, 12);

  return (
    <div className="stack">
      <div>
        <h2 className="h-section">{cand.name ?? 'Candidate'}</h2>
        <p className="cell-sub m-0">
          {cand.academic?.program && <>{cand.academic.program}</>}
          {cand.academic?.specialization && <> · {cand.academic.specialization}</>}
          {cand.academic?.expectedGraduationYear && <> · Batch {cand.academic.expectedGraduationYear}</>}
        </p>
        {cand.email && <p className="cell-sub m-0">{cand.email}{cand.phone && <> · {cand.phone}</>}</p>}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="card kpi">
          <span className="kpi-label">CGPA</span>
          <span className="kpi-value">{typeof cand.academic?.cgpa === 'number' ? cand.academic.cgpa.toFixed(2) : '—'}</span>
          {typeof cand.academic?.backlogs === 'number' && (
            <span className="kpi-meta">{cand.academic.backlogs} backlog{cand.academic.backlogs === 1 ? '' : 's'}</span>
          )}
        </div>
        <div className="card kpi">
          <span className="kpi-label">Match score</span>
          <span className="kpi-value">
            {typeof app?.matchScore?.overall === 'number' ? `${app.matchScore.overall}%` : '—'}
          </span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Resume</span>
          <span className="kpi-value">{resume?.atsScore ?? '—'}</span>
          <span className="kpi-meta">{resume?.name ?? 'no primary resume'}</span>
        </div>
      </div>

      {app?.matchScore?.reasoning && (
        <div className="notice">
          <b>Why this score</b>
          <div>{app.matchScore.reasoning}</div>
        </div>
      )}

      {resume?.summary && (
        <div>
          <div className="cell-sub mb-1">Resume summary</div>
          <p className="m-0">{resume.summary}</p>
        </div>
      )}

      {skillLabels.length > 0 && (
        <div>
          <div className="cell-sub mb-1">Skills</div>
          <div className="flex flex-wrap gap-1">
            {skillLabels.map((s, i) => <span key={i} className="badge">{s}</span>)}
          </div>
        </div>
      )}

      {(cand.projects ?? []).length > 0 && (
        <div>
          <div className="cell-sub mb-1">Recent projects</div>
          <ol className="stack">
            {cand.projects!.map((p, i) => (
              <li key={i} className="p-3 border border-line-tertiary rounded">
                <div className="font-medium">{p.title ?? 'Project'}</div>
                {p.description && <div className="cell-sub">{p.description}</div>}
                {p.techStack && p.techStack.length > 0 && (
                  <div className="cell-sub mt-1">{p.techStack.join(', ')}</div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {(cand.certifications ?? []).length > 0 && (
        <div>
          <div className="cell-sub mb-1">Certifications</div>
          <div className="flex flex-wrap gap-1">
            {cand.certifications!.map((c, i) => (
              <span key={i} className="badge">
                {c.name ?? '—'}{c.issuer ? ` · ${c.issuer}` : ''}{c.year ? ` · ${c.year}` : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {d.rounds.length > 1 && (
        <div>
          <div className="cell-sub mb-1">This candidate's rounds in the drive</div>
          <ol className="stack">
            {d.rounds.map((rd) => (
              <li key={rd._id} className="flex items-center gap-2">
                <span className="badge">Round {rd.roundNumber}</span>
                <span>{rd.roundType ?? '—'}</span>
                <span className="cell-sub ml-auto">
                  {rd.scheduledAt && formatIndianDate(new Date(rd.scheduledAt))}
                  {rd.status && ` · ${rd.status}`}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
